export type Token =
  | { type: 'text'; text: string }
  | { type: 'link'; text: string; href: string }
  | { type: 'mention'; text: string; username: string; isMe: boolean }
  | { type: 'bold'; children: Token[] }
  | { type: 'code'; text: string }

export interface TokenizeOptions {
  /** Current user's username, for stronger highlighting. */
  meUsername?: string | null
}

const INLINE_PATTERN =
  /(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+)|(^|[^\w@.])@([A-Za-z0-9][A-Za-z0-9._-]*)/g

const TRAILING_PUNCTUATION = /[.,;:!?'"]+$/

function trimUrl(raw: string): string {
  let url = raw.replace(TRAILING_PUNCTUATION, '')
  // Drop unbalanced closing parentheses, e.g. "(see https://x.test/a)".
  while (url.endsWith(')') && (url.match(/\(/g)?.length ?? 0) < (url.match(/\)/g)?.length ?? 0)) {
    url = url.slice(0, -1).replace(TRAILING_PUNCTUATION, '')
  }
  return url
}

function pushText(tokens: Token[], text: string) {
  if (!text) return
  const last = tokens[tokens.length - 1]
  if (last?.type === 'text') last.text += text
  else tokens.push({ type: 'text', text })
}

/** Links and @mentions inside plain text. */
function tokenizeInline(text: string, options: TokenizeOptions): Token[] {
  const tokens: Token[] = []
  let index = 0
  for (const match of text.matchAll(INLINE_PATTERN)) {
    const start = match.index ?? 0
    if (match[1]) {
      const url = trimUrl(match[1])
      if (!url || url === 'www.') continue
      pushText(tokens, text.slice(index, start))
      const href = url.startsWith('www.') ? `https://${url}` : url
      tokens.push({ type: 'link', text: url, href })
      index = start + url.length
      continue
    }
    const prefix = match[2] ?? ''
    const username = (match[3] ?? '').replace(/[._-]+$/, '')
    if (!username) continue
    const mentionStart = start + prefix.length
    pushText(tokens, text.slice(index, mentionStart))
    const isMe = Boolean(options.meUsername) && username.toLowerCase() === options.meUsername!.toLowerCase()
    tokens.push({ type: 'mention', text: `@${username}`, username, isMe })
    index = mentionStart + 1 + username.length
  }
  pushText(tokens, text.slice(index))
  return tokens
}

/** *bold* spans (single line, no surrounding spaces) then inline tokens. */
function tokenizeBold(text: string, options: TokenizeOptions): Token[] {
  const tokens: Token[] = []
  const pattern = /(^|[^\w*])\*(?=\S)([^*\n]*?\S)\*(?![\w*])/g
  let index = 0
  for (const match of text.matchAll(pattern)) {
    const start = (match.index ?? 0) + match[1].length
    // Do not split a URL that happens to contain asterisks.
    const before = text.slice(0, start)
    if (/https?:\/\/\S*$/.test(before)) continue
    for (const t of tokenizeInline(text.slice(index, start), options)) mergePush(tokens, t)
    tokens.push({ type: 'bold', children: tokenizeInline(match[2], options) })
    index = start + match[2].length + 2
  }
  for (const t of tokenizeInline(text.slice(index), options)) mergePush(tokens, t)
  return tokens
}

function mergePush(tokens: Token[], token: Token) {
  if (token.type === 'text') pushText(tokens, token.text)
  else tokens.push(token)
}

/**
 * Tokenize a message body for rendering as React elements (never HTML).
 * Order of precedence: `code` spans, then *bold*, then links and @mentions.
 * Newlines stay inside text tokens; the bubble uses `white-space: pre-wrap`.
 */
export function tokenize(body: string, options: TokenizeOptions = {}): Token[] {
  const tokens: Token[] = []
  const codePattern = /`([^`\n]+)`/g
  let index = 0
  for (const match of body.matchAll(codePattern)) {
    const start = match.index ?? 0
    for (const t of tokenizeBold(body.slice(index, start), options)) mergePush(tokens, t)
    tokens.push({ type: 'code', text: match[1] })
    index = start + match[0].length
  }
  for (const t of tokenizeBold(body.slice(index), options)) mergePush(tokens, t)
  return tokens
}

/** Usernames mentioned in a body (for notifications/tests). */
export function mentionedUsernames(body: string): string[] {
  const names: string[] = []
  const walk = (tokens: Token[]) => {
    for (const t of tokens) {
      if (t.type === 'mention') names.push(t.username)
      else if (t.type === 'bold') walk(t.children)
    }
  }
  walk(tokenize(body))
  return [...new Set(names)]
}
